/**
 * Display/query time window for the live report pages (Live Maneuvers, Live Performance).
 *
 * Deliberately NOT `constants/liveRetention.ts` and NOT `playbackStore.timeWindow`: both of those
 * hard-cap at 30 minutes (LIVE_TIME_WINDOW_MAX_MINUTES, and playbackStore clamps whenever
 * liveMode()), because they describe the live map's stream-ring backfill depth. This window is a
 * table/chart scope and needs to reach 60 minutes and "everything".
 *
 * The window is both the *display* scope and the *query* scope. That equivalence is what makes it
 * safe: the live maneuver poll merges rows by event_id with `{...prev, ...row}`, so it also picks
 * up server-side re-grades of older maneuvers. Restricting the request to the window means
 * re-grades inside the window still refresh, and rows outside it are not shown anyway.
 */

/** Window value meaning "no lower bound" — the whole live day, i.e. today's behaviour. */
export const LIVE_REPORT_WINDOW_ALL = 0;

/** Default window for the live report pages: the last hour. */
export const LIVE_REPORT_WINDOW_DEFAULT_MINUTES = 60;

/** Coerce a persisted/shared value to a valid window. Anything unusable falls back to the default. */
export function normalizeLiveReportWindowMinutes(value: unknown): number {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) return LIVE_REPORT_WINDOW_DEFAULT_MINUTES;
  if (minutes === 0) return LIVE_REPORT_WINDOW_ALL;
  return minutes;
}

export function isLiveReportWindowAll(windowMinutes: number): boolean {
  return normalizeLiveReportWindowMinutes(windowMinutes) === LIVE_REPORT_WINDOW_ALL;
}

/** Epoch ms lower bound for the window, or null for "All". */
export function liveReportWindowStartMs(windowMinutes: number, nowMs: number = Date.now()): number | null {
  const minutes = normalizeLiveReportWindowMinutes(windowMinutes);
  if (minutes === LIVE_REPORT_WINDOW_ALL) return null;
  return nowMs - minutes * 60_000;
}

/**
 * ISO 8601 lower bound for the `start_time` query param, or null for "All".
 * Null must mean "omit the param" so the request URL stays byte-identical to the pre-window one.
 */
export function liveReportWindowStartIso(windowMinutes: number, nowMs: number = Date.now()): string | null {
  const startMs = liveReportWindowStartMs(windowMinutes, nowMs);
  return startMs == null ? null : new Date(startMs).toISOString();
}

/** Short label for the settings pill. */
export function liveReportWindowLabel(windowMinutes: number): string {
  const minutes = normalizeLiveReportWindowMinutes(windowMinutes);
  if (minutes === LIVE_REPORT_WINDOW_ALL) return 'ALL';
  if (minutes === LIVE_REPORT_WINDOW_DEFAULT_MINUTES) return 'LAST HOUR';
  return `${minutes} MIN`;
}
